import {
  Component,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { Store } from '@ngrx/store';

import { MerchantBasicDataViewModel } from '@app/api/marketplace/models';
import { MerchantSelectors } from '@merchant/store/merchant.slice';

import { required } from '../../decorators/required.decorator';
import { getServerErrorForField } from '../../helpers/form-helper';
import { notBlank } from '../../validators/not-blank.validator';
import { EasyLocalizedValueSubformComponent } from '../easy-localized-value-subform/easy-localized-value-subform.component';
import { EasyPhoneSubformComponent } from '../easy-phone-subform/easy-phone-subform.component';

@Component({
  selector: 'app-localized-key-value-pairs-subform',
  templateUrl: './localized-key-value-pairs-subform.component.html',
  styleUrls: ['./localized-key-value-pairs-subform.component.scss'],
})
export class LocalizedKeyValuePairsSubformComponent
  implements OnInit, OnChanges
{
  // general
  @Input() @required formArrayName!: string;
  @Input() @required title!: string;

  // value
  @Input() valueSubformGroupName: string = 'title';
  @Input() valueControlValidators: ValidatorFn[] = [
    Validators.required,
    notBlank,
  ];

  // key
  @Input() @required keyControlName!: string;
  @Input() keyControlValidators: ValidatorFn[] = [
    Validators.required,
    notBlank,
  ];
  @Input() keyControlTitle!: string;
  @Input() keyControlValidatorMessage?: string;
  @Input() keyIsPhoneNumber = false;

  @ViewChildren(EasyPhoneSubformComponent)
  phoneSubformComponents!: QueryList<EasyPhoneSubformComponent>;

  formArray!: UntypedFormArray;

  basicData?: MerchantBasicDataViewModel;

  constructor(
    readonly rootFormGroup: FormGroupDirective,
    private readonly fb: UntypedFormBuilder,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store.select(MerchantSelectors.selectBasicData).subscribe((data) => {
      this.basicData = data;
    });
  }

  ngOnChanges(): void {
    this.formArray = this.rootFormGroup.control.get(
      this.formArrayName
    ) as UntypedFormArray;
  }

  onAdd() {
    this.formArray.markAsDirty();
    this.formArray.push(
      this.fb.group({
        [this.valueSubformGroupName]: this.fb.group({
          [EasyLocalizedValueSubformComponent.DEFAULT_LANGUAGE]:
            new UntypedFormControl(null, this.valueControlValidators),
        }),
        [this.keyControlName]: new UntypedFormControl(
          null,
          this.keyControlValidators
        ),
      })
    );
  }

  onDelete(index: number) {
    this.formArray.markAsDirty();
    this.formArray.removeAt(index);
  }

  get valueSubform(): UntypedFormGroup {
    return this.formArray.get(this.valueSubformGroupName) as UntypedFormGroup;
  }

  get keyControl(): UntypedFormControl {
    return this.formArray.get(this.keyControlName) as UntypedFormControl;
  }

  getServerError(fieldName: string, index: number): any {
    return getServerErrorForField(
      this.rootFormGroup.form,
      `${this.formArrayName}.${index}.${fieldName}`
    );
  }

  useBasicDataPhone(event: MatCheckboxChange) {
    if (event.checked) {
      let first = this.phoneSubformComponents.get(0);
      if (first) {
        first.useBasicDataPhone(this.basicData?.phone);
      } else {
        this.onAdd();
        this.phoneSubformComponents
          .get(0)
          ?.useBasicDataPhone(this.basicData?.phone);
      }
      this.formArray.controls[0].disable();
    } else {
      this.formArray.controls[0].enable();
      this.formArray.controls[0].setValue({
        phone: null,
        title: { hun: null },
      });
    }
  }

  castToForm(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup;
  }
}
